<template lang="pug">
  .newcomer-task-list
    .coin-tag-wrap
      .coin-tag
        img.coin-icon(src="https://m.hellobike.com/fd018348_new_coin.png")
        span +{{ totalReward }}
    .task-items(
      ref="taskList"
    )
      .task-item(
        v-for="(task, index) in tasks"
        :key="`${task.taskCode}-${task.actionStep}`"
        :class="getTaskBtnClass(task)"
        @click="handleTaskClick(task, index)"
      )
        .task-name {{ task.taskName }}
        .task-reward
          img.coin-icon(src="https://m.hellobike.com/fd018348_new_coin.png")
          span +{{ task.offerWorth || 0 }}
        .task-btn(
          :class="{\
            'can-claim': task.actionStep === ACTION_STEP.CAN_CLAIM,\
            'task-btn--wide': tasks.length < 3,\
          }"
        )
          | {{ task.actionStep === ACTION_STEP.CLAIMED ? '已完成' : task.btnName }}
        //- 签到任务手指动画：未完成时展示，5秒后消失
        Lottie.finger-lottie(
          v-if="showFingerHint && task.isSignTask && task.actionStep === ACTION_STEP.DOING"
          url="https://m.hellobike.com/resource/helloyun/08345/DAcMKLrQ3U.json"
        )
</template>

<script>
import { env } from '@hb/NBridge/lib/h5';
import { getTokenAndKey, scrollToLeft } from '@/utils';
import { getVersion } from '@/utils/systemCode';
import { getTaskAward } from '@/api/bounty';
import Toast from '@/components/toast';
import eventBus from '@/const/eventBus';
import { ParamsUtil } from '@/utils/params';
import { mapTaskV2 } from '@/utils/mappers';
import { TaskStatus, TASK_STATUS_V2 } from '@/const/task';
import {
  loadPlatformAds,
  showPlatformAds,
} from '../../../../common/bridge';
import {
  processAdParams,
  loadRewardResultPlatformAds,
} from '../../../../common/helper';
import SPM from '../../../../common/spm';
import { handleTaskBtnClick } from '../../../../common/task';
import handleDoingTask from '../common/task-handler';

// actionStep: 0-任务在做未领奖, 1-已完成未领奖(可领奖), 2-已完成已领奖
const ACTION_STEP = { DOING: 0, CAN_CLAIM: 1, CLAIMED: 2 };

export default {
  name: 'NewcomerTaskList',
  components: {
    Lottie: () => import('@/components/lottie'),
  },
  props: {
    tasks: {
      type: Array,
      default: () => [],
    },
    totalReward: {
      type: Number,
      default: 0,
    },
    taskDeadLine: {
      type: Number,
      default: 0,
    },
  },
  inject: {
    isSupportAd: { default: () => () => false },
  },
  data() {
    return {
      ACTION_STEP,
      // 领奖后待滚动标记，tasks 刷新后触发滚动
      pendingScroll: false,
      // 跨端跳转降级定时器
      jumpTimer: null,
      // 签到任务手指动画显示状态
      showFingerHint: false,
      // 手指动画定时器
      fingerHintTimer: null,
    };
  },
  watch: {
    tasks() {
      if (this.pendingScroll) {
        this.pendingScroll = false;
        this.$nextTick(() => {
          this.scrollToNextTask();
        });
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToNextTask();
      // 上报任务卡片曝光埋点（每个任务单独上报）
      this.trackTasksExpose();
    });
    // 签到完成后触发滚动
    eventBus.$on('refreshNewcomerGuide', this.handleSignRefresh);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pagehide', this.clearJumpTimer);
    // 签到任务手指动画：有未完成的签到任务时展示，5秒后消失
    this.startFingerHint();
  },
  destroyed() {
    eventBus.$off('refreshNewcomerGuide', this.handleSignRefresh);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.clearJumpTimer);
    this.clearJumpTimer();
    this.clearFingerHintTimer();
  },
  methods: {
    // 跨端跳转降级定时器清理
    clearJumpTimer() {
      if (this.jumpTimer) {
        clearTimeout(this.jumpTimer);
        this.jumpTimer = null;
      }
    },
    // 手指动画定时器清理
    clearFingerHintTimer() {
      if (this.fingerHintTimer) {
        clearTimeout(this.fingerHintTimer);
        this.fingerHintTimer = null;
      }
    },
    // 启动签到任务手指动画：有未完成签到任务时展示，5秒后消失
    startFingerHint() {
      const hasUnfinishedSignTask = this.tasks.some(
        (t) => t.isSignTask && t.actionStep === ACTION_STEP.DOING,
      );
      if (!hasUnfinishedSignTask) return;
      this.showFingerHint = true;
      this.fingerHintTimer = setTimeout(() => {
        this.showFingerHint = false;
        this.fingerHintTimer = null;
      }, 5000);
    },
    // 页面隐藏时清理跳转定时器
    handleVisibilityChange() {
      if (document.hidden || document.webkitHidden) this.clearJumpTimer();
    },
    // 签到完成后标记待滚动
    handleSignRefresh() {
      this.pendingScroll = true;
    },
    // 找到第一个未完成任务，将其滚动到视口左边
    scrollToNextTask() {
      if (this.tasks.length < 4) return;
      const firstUnfinished = this.tasks.findIndex(
        (t) => !(t.taskStatus === 'FINISHED' && t.actionStep === ACTION_STEP.CLAIMED),
      );
      if (firstUnfinished < 0) return;
      const items = this.$refs.taskList.querySelectorAll('.task-item');
      if (items[firstUnfinished]) {
        scrollToLeft(items[firstUnfinished], this.$refs.taskList);
      }
    },
    // 获取任务状态文本（用于埋点），与按钮状态保持一致：进行中、未领奖、已完成
    getTaskStateText(task) {
      const { taskStatus, actionStep } = task;
      if (taskStatus === TASK_STATUS_V2.FINISHED && actionStep === ACTION_STEP.CLAIMED) return '已完成';
      if (taskStatus === TASK_STATUS_V2.FINISHED && actionStep === ACTION_STEP.CAN_CLAIM) return '未领奖';
      return '进行中';
    },
    // 上报任务卡片曝光埋点（每个任务单独上报）
    trackTasksExpose() {
      this.tasks.forEach((task) => {
        SPM.moduleExpose({
          moduleId: 'platform_newcomerTask',
          contentId: 'platform_newcomerTask',
          businessInfo: {
            taskBonus: task.offerWorth,
            task_name: task.taskName || '',
            taskState: this.getTaskStateText(task),
            taskid: task.taskCode,
            remainingDays: String(task.expireDate || ''),
          },
        });
      });
    },
    getTaskBtnClass(task) {
      const { taskStatus, actionStep } = task;
      return {
        'is-done': taskStatus === TASK_STATUS_V2.FINISHED && actionStep === ACTION_STEP.CLAIMED,
      };
    },
    async handleTaskClick(task, index) {
      // actionStep === 2：已领奖，不可点击
      if (
        task.taskStatus === TASK_STATUS_V2.FINISHED
        && task.actionStep === ACTION_STEP.CLAIMED
      ) return;
      // 上报任务卡片点击埋点
      SPM.clickButton({
        buttonName: 'platform_newcomerTask',
        businessInfo: {
          taskState: this.getTaskStateText(task),
          taskBonus: task.offerWorth,
          task_name: task.taskName || '',
          taskid: task.taskCode,
          remainingDays: String(task.expireDate || ''),
        },
      });
      if (env.isEasyBikeApp) {
        await this.clickTaskV2(task, index);
      } else {
        handleTaskBtnClick.call(this, task, { scene: 'task_list', needReceiveCoupon: true });
      }
    },

    async clickTaskV2(task, index) {
      if (!task || task.disabled) return;

      const adaptedTask = { ...mapTaskV2(task), ...task, amount: task.offerWorth || 0 };
      const { status } = adaptedTask;

      if (status === TaskStatus.CAN_DONE) {
        const { token } = await getTokenAndKey();
        const { taskGuid, taskGroupCode, amount } = adaptedTask;
        try {
          await getTaskAward({
            token,
            channelId: env.platformCode,
            taskGuid,
            taskGroupCode,
            version: await getVersion(),
          });
          Toast.show(`任务奖励领取成功，+${amount}奖励金`);
          if (this.isSupportAd()) {
            await this.handleLoadTaskAwardPopup({ rewardCount: amount, task: adaptedTask, index });
          } else {
            eventBus.$emit('playCollectAnimate');
          }
          this.$emit('refreshGuide');
          // 记录待滚动标记，等 tasks 刷新后执行
          this.pendingScroll = true;
        } catch (e) {
          Toast.show((e && e.message) || '任务奖励领取失败');
        }
        return;
      }

      if (status === TaskStatus.DOING) {
        if (adaptedTask.isSignTask) {
          SPM.clickButton({
            buttonName: 'platform_taskGoto',
            businessInfo: {
              taskid: adaptedTask.taskCode,
              task_name: adaptedTask.taskName || '',
              taskBonus: adaptedTask.offerWorth,
              taskState: this.getTaskStateText(adaptedTask),
            },
          });
          // 签到任务：拉起签到弹窗，签到完成后刷新新人引导数据
          eventBus.$emit('showSignInPopup');
        } else {
          this.jumpTimer = await handleDoingTask(adaptedTask, this.jumpTimer);
        }
      }
    },

    async handleLoadTaskAwardPopup(options) {
      const { rewardCount, task = {}, index = -1 } = options || {};
      const adsInfo = processAdParams({});
      const loadRes = await loadPlatformAds({
        pageName: 'rewardResult',
        timeout: '2',
        scene: 'rebateBonus',
        adsource: ParamsUtil.getParams().adsource || '',
        adsInfo,
        otherInfo: { data: { rewardCount } },
      });

      // 弹窗基础埋点信息
      const popupBaseInfo = {
        task_name: task.title || task.taskName || '',
        taskId: task.taskCode || '',
        ladderIndex: index >= 0 ? `${index + 1}` : '',
      };

      if (loadRes && loadRes.pageId) {
        eventBus.$emit('playCollectAnimate');
        SPM.moduleExpose({
          moduleId: 'Welfare_taskcompop',
          contentId: 'Welfare_taskcompop',
          businessInfo: {
            ...popupBaseInfo,
            impids: adsInfo.adUnitId || '',
            adv: adsInfo.adUnitId && adsInfo.rewardCount ? '0' : '1',
          },
        });
        const showRes = await showPlatformAds({ pageId: loadRes.pageId });
        const { action, inspireType, adsInfo: { rewardTotalCount = '' } = {} } = showRes || {};
        if (action === 'bannerReward') {
          loadRewardResultPlatformAds({
            inspireType,
            bannerTaskRelationCouponInfo: null,
            otherInfo: { data: { rewardCount: rewardTotalCount } },
          });
          SPM.clickButton({
            buttonName: 'Welfare_taskcompop_adv',
            businessInfo: {
              ...popupBaseInfo,
              impids: adsInfo.adUnitId || '',
            },
          });
        } else if (action === 'close') {
          SPM.clickButton({
            buttonName: 'Welfare_taskcompop_close',
            businessInfo: {
              ...popupBaseInfo,
              impids: adsInfo.adUnitId || '',
            },
          });
        }
      }
    },
  },
};
</script>

<style lang="less" scoped>
.newcomer-task-list {
  margin-top: 0.14rem;
  width: 100%;
  height: 1.51rem;
  border-radius: 0.15rem;
  background: url('https://m.hellobike.com/aafb3b03f5.png') no-repeat top center / 100% auto;
  padding: 0.39rem 0.08rem 0.08rem;
  box-sizing: border-box;
  position: relative;

  .coin-tag-wrap {
    position: absolute;
    top: 0.12rem;
    left: 0.78rem;
    display: flex;
    align-items: center;
    gap: 0.06rem;

    .coin-tag {
      display: flex;
      align-items: center;
      height: 0.2rem;
      background: linear-gradient(90deg, #fcf0cb 0%, #fcf2b1 100%);
      border-radius: 0.12rem;
      padding: 0 0.05rem 0 0.04rem;

      .coin-icon {
        width: 0.14rem;
        height: 0.14rem;
        margin-right: 0.01rem;
        flex-shrink: 0;
      }

      span {
        font-size: 0.14rem;
        font-family: 'DIN Alternate Bold', sans-serif;
        font-weight: 700;
        color: #833800;
        opacity: 0.9;
      }
    }
  }

  .task-deadline {
    position: absolute;
    top: 0.16rem;
    right: 0.12rem;
    font-size: 0.12rem;
    font-family: 'PingFang SC', sans-serif;
    font-weight: 400;
    color: #fff;
    text-align: right;
  }

  .task-items {
    display: flex;
    gap: 0.06rem;
    overflow-x: auto;
    overflow-y: clip;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .task-item {
      flex: 1 0 0.94rem;
      max-width: 1.56rem;
      height: 1.04rem;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: linear-gradient(180deg, #fffcf6 100%, #fff1d0 0%);
      border-radius: 0.12rem;
      padding: 0.08rem 0.04rem;
      box-sizing: border-box;
      cursor: pointer;
      position: relative;
      margin-bottom: 0.12rem;

      .task-name {
        font-size: 0.12rem;
        font-weight: 500;
        font-family: 'PingFang SC', sans-serif;
        color: #242729;
        text-align: center;
        margin-bottom: 0.04rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
      }

      .task-reward {
        display: flex;
        align-items: center;
        margin-bottom: 0.06rem;

        .coin-icon {
          width: 0.14rem;
          height: 0.14rem;
          margin-right: 0.02rem;
          flex-shrink: 0;
        }

        span {
          font-size: 0.18rem;
          font-family: 'DIN Alternate Bold', sans-serif;
          font-weight: 700;
          color: #fe852a;
        }
      }

      .task-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 0.62rem;

        &--wide {
          width: 0.96rem;
        }

        height: 0.28rem;
        line-height: 0.28rem;
        border-radius: 0.135rem;
        font-size: 0.12rem;
        font-weight: 500;
        pointer-events: none;
        background: linear-gradient(90deg, #ff7753 0%, #fe4043 100%);
        color: #fff;

        // 可领奖状态
        &.can-claim {
          background: linear-gradient(90deg, #ffe578 0%, #ffe060 100%);
          color: #000;
        }
      }

      // 已完成任务
      &.is-done {
        .task-reward {
          opacity: 0.5;
        }

        .task-name {
          opacity: 0.5;
        }

        .task-btn {
          opacity: 0.1;
          background: #242729;
          color: #fff;
        }
      }

      .finger-lottie {
        position: absolute;
        bottom: -0.23rem;
        right: 0;
        width: 1.6rem;
        pointer-events: none;
        z-index: 2;
      }
    }
  }
}
</style>
